import type { AppRole, AvatarTint, SessionUser } from './types';
import type { IdentityClaim } from './wagepro_store_identity';

/**
 * The real-backend facts this pass can source for the signed-in session,
 * once an entity context (firm_id/entity_id) is known. Permanent adapter
 * shape — survives the #21929 employer-admin OAuth scope work unchanged;
 * only HOW these facts get resolved changes (see `./use_session_facts.ts`
 * for today's real bearer-client resolver, live in every environment
 * including production — the former dev-bypass-only `dev_session_facts.ts`
 * this replaced was deleted by C1, #22323).
 */
export interface SessionFacts {
  /** `entity_memberships.payroll_role` via `entityUser.me` (`perms.payroll_role`). */
  payroll_role: 'none' | 'employee' | 'admin';
  /** `entityUser.me`'s derived `is_adviser` (base_role === 'adviser'). */
  is_adviser: boolean;
  /**
   * `entityUser.me`'s `perms.manage_users` — the real, already-resolved
   * "can manage users on this entity" permission (independent of the
   * payroll-specific `payroll_role`). Used as the admin-equivalent signal
   * for the `payroll_role === 'none'` fallback in `deriveRole` below, so
   * that fallback derives from a real entity permission instead of a
   * mock/base default (see `deriveRole`'s docblock).
   */
  manage_users: boolean;
  /**
   * `entityUser.me.role` — the caller's `EntityRole` on this entity
   * (`admin`/`standard`/`readonly`/…). Threaded through so
   * `ConnectLandingGate` can mirror `wageproAuth.connectOrganisation`'s
   * server-side "firm owner/admin OR entity admin" rule (wagepro_auth.ts
   * ~L1030) instead of inferring "admin-like" from
   * `payroll_role`/`is_adviser`/`manage_users` — that inference flags every
   * firm staffer as admin-like (including readonly), so the setup CTA
   * appears for accounts the server then rejects with FORBIDDEN.
   */
  entity_role: string;
  /**
   * `entityUser.me.firm_role` — the caller's `firm_memberships.role` on the
   * firm they access this entity through. Same purpose as {@link entity_role}
   * above; `entityProcedure`'s middleware synthesises `'member'` when the
   * caller has no firm membership (direct-client user), so `'owner'`/
   * `'admin'` here match the server-side check without further translation.
   */
  firm_role: string;
  /** `entities.name` via `entity.clientGet`. */
  company_name: string;
}

/** Deterministic (never random) `AvatarTint` selection, keyed by user id. */
const AVATAR_TINTS: readonly AvatarTint[] = [
  'blue',
  'green',
  'amber',
  'rose',
  'violet',
  'slate',
  'teal',
];

/**
 * A tiny deterministic string hash (FNV-1a-shaped, 32-bit) — not
 * cryptographic, just needs to spread `userId` values evenly across the
 * fixed `AVATAR_TINTS` list so the same user always gets the same tint
 * across reloads/sessions without a database column dedicated to it.
 */
function hashUserId(userId: string): number {
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < userId.length; i++) {
    hash ^= userId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime
  }
  return hash >>> 0;
}

/**
 * Deterministic (never random) `AvatarTint` selection keyed by an arbitrary
 * string (a user id, an employee id, ...). Generalized from this module's
 * original `deriveTint` (which only ever hashed `identity.userId`) so
 * `wagepro_store_employees.ts` (C3, #21934) can derive a per-employee tint
 * from `employee.id` using the SAME hash function instead of duplicating
 * `hashUserId`/`AVATAR_TINTS` — one source of truth for "how a stable id
 * maps to a tint" across the whole app.
 */
export function deriveTintFromKey(
  key: string,
  base: AvatarTint = 'slate',
): AvatarTint {
  const index = hashUserId(key) % AVATAR_TINTS.length;
  return AVATAR_TINTS[index] ?? base;
}

function deriveTint(
  base: AvatarTint,
  identity: IdentityClaim | undefined,
): AvatarTint {
  if (!identity) return base;
  return deriveTintFromKey(identity.userId, base);
}

/**
 * Maps `payroll_role` (+ `is_adviser`/`manage_users`) onto the UI's binary
 * `AppRole`.
 *
 * `'admin'` / `'employee'` map directly from `payroll_role`. `'none'` is the
 * fallback branch every REAL membership resolves to today:
 * `assertEntityRoleAllowedForTier` (`apps/api/src/trpc/routers/entity_users.ts`)
 * rejects any non-`'none'` `payroll_role` on every plan tier (payroll hasn't
 * shipped as a sellable module yet), so no real account can hold
 * `'admin'`/`'employee'` via the API today — only via direct DB writes
 * (existing backend tests already do this).
 *
 * For that `'none'` case, this derives a role from REAL entity-level
 * permission signals `entityUser.me` already resolved in the SAME call
 * (`is_adviser`, `manage_users`) — never from a mock/base default. There is
 * genuinely no mock fallback here (fixed 2026-07 — see PR #22341 adversarial
 * review finding #3: the previous version of this function fell back to
 * `base` — the caller's pre-existing role, which for every un-migrated call
 * site is `MOCK_SESSION.role === 'admin'` — so every real non-adviser
 * employer user was silently shown 'admin', contradicting
 * `use_session_facts.ts`'s "no mock fallback" claim). An adviser (the
 * firm/entity creator, e.g. a freshly auto-provisioned dev account) OR
 * anyone holding the real `manage_users` permission is treated as `'admin'`
 * since either holds full entity access; everyone else gets the narrower
 * `'employee'` (self-service) view — the correct least-privilege default,
 * not a demotion. Revisit this fallback once the tier gate lifts and real
 * `payroll_role` values become reachable.
 */
function deriveRole(
  facts: Pick<SessionFacts, 'payroll_role' | 'is_adviser' | 'manage_users'>,
): AppRole {
  if (facts.payroll_role === 'admin') return 'admin';
  if (facts.payroll_role === 'employee') return 'employee';
  return facts.is_adviser || facts.manage_users ? 'admin' : 'employee';
}

/**
 * Overlays real `SessionFacts` onto a session that has already had
 * `deriveSessionFromIdentity` applied (so `name`/`initials` stay
 * identity-derived — this function never touches them). Returns `base`
 * completely unchanged when `facts` is absent, matching
 * `deriveSessionFromIdentity`'s "absent input -> no-op" contract so an
 * un-migrated call site (or a dev boot where the facts resolver failed)
 * keeps today's fully-mocked `role`/`tint`/`company_name` instead of
 * silently going undefined.
 */
export function deriveSessionFromFacts(
  base: SessionUser,
  identity: IdentityClaim | undefined,
  facts: SessionFacts | undefined,
): SessionUser {
  if (!facts) return base;

  return {
    ...base,
    role: deriveRole(facts),
    company_name:
      // `typeof` guard, not just a truthiness check: entities.name is
      // non-null in the schema today, but `facts` crosses a network
      // boundary, and a malformed/absent value must fall back to the mock
      // name instead of throwing inside this useState initializer (which
      // would white-screen the app rather than degrade). Unlike `role`
      // above (which used to fall back to `base.role` on every routine
      // `payroll_role === 'none'` response — now fixed), this branch is a
      // defensive guard against a genuinely malformed/blank server
      // response, not a routine default: `entity.clientGet` returns a
      // non-blank `entities.name` on every real response today.
      typeof facts.company_name === 'string' &&
      facts.company_name.trim().length > 0
        ? facts.company_name
        : base.company_name,
    tint: deriveTint(base.tint, identity),
  };
}
